import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, CopyIcon, UsersIcon, FileTextIcon, MoreVerticalIcon, BookOpenIcon, FlaskConicalIcon, PencilIcon, LandmarkIcon, GlobeIcon, PaletteIcon, MusicIcon, CodeIcon, ActivityIcon, DnaIcon, CalculatorIcon, BeakerIcon, BookIcon, MapIcon, BrushIcon, MicIcon, CpuIcon, TrophyIcon, HeartPulseIcon, BoxIcon, HeartIcon, ListChecksIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
// Icon mapping function based on title and tags
const getIconForAssessment = (title: string, tags: string[]): any => {
  const lowerTitle = title.toLowerCase();
  const lowerTags = tags.map(tag => tag.toLowerCase()).join(' ');
  const combined = `${lowerTitle} ${lowerTags}`;
  // Math-related
  if (combined.includes('math') || combined.includes('algebra') || combined.includes('fraction') || combined.includes('calculation')) {
    return CalculatorIcon;
  }
  // Science-related
  if (combined.includes('science') || combined.includes('physics') || combined.includes('chemistry') || combined.includes('matter')) {
    return BeakerIcon;
  }
  // Biology-related
  if (combined.includes('biology') || combined.includes('body') || combined.includes('human') || combined.includes('dna')) {
    return DnaIcon;
  }
  // English/Grammar
  if (combined.includes('english') || combined.includes('grammar') || combined.includes('writing') || combined.includes('literature')) {
    return PencilIcon;
  }
  // History
  if (combined.includes('history') || combined.includes('civilization') || combined.includes('ancient')) {
    return LandmarkIcon;
  }
  // Geography
  if (combined.includes('geography') || combined.includes('continent') || combined.includes('ocean') || combined.includes('map')) {
    return GlobeIcon;
  }
  // Art
  if (combined.includes('art') || combined.includes('painting') || combined.includes('renaissance')) {
    return PaletteIcon;
  }
  // Music
  if (combined.includes('music') || combined.includes('theory') || combined.includes('note')) {
    return MusicIcon;
  }
  // Computer Science/Programming
  if (combined.includes('computer') || combined.includes('programming') || combined.includes('algorithm') || combined.includes('code')) {
    return CodeIcon;
  }
  // Physical Education/Sports
  if (combined.includes('physical') || combined.includes('sport') || combined.includes('pe') || combined.includes('team')) {
    return ActivityIcon;
  }
  // Default fallback
  return BookOpenIcon;
};
const getIconColor = (title: string, tags: string[]): string => {
  const lowerTitle = title.toLowerCase();
  const lowerTags = tags.map(tag => tag.toLowerCase()).join(' ');
  const combined = `${lowerTitle} ${lowerTags}`;
  if (combined.includes('math') || combined.includes('algebra')) return '#2563EB';
  if (combined.includes('science') || combined.includes('chemistry')) return '#00B75F';
  if (combined.includes('biology') || combined.includes('dna')) return '#8B5CF6';
  if (combined.includes('english') || combined.includes('grammar')) return '#FF9D33';
  if (combined.includes('history')) return '#FF6CA1';
  if (combined.includes('geography')) return '#2563EB';
  if (combined.includes('art')) return '#FF6CA1';
  if (combined.includes('music')) return '#8B5CF6';
  if (combined.includes('computer') || combined.includes('programming')) return '#2563EB';
  if (combined.includes('physical') || combined.includes('sport')) return '#00B75F';
  return '#00B75F';
};
// Consistent tag color function - cycles through brand colors
const getTagColor = (index: number) => {
  const colors = ['bg-[#2563EB] bg-opacity-10 text-[#2563EB]', 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]', 'bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]', 'bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1]', 'bg-[#8B5CF6] bg-opacity-10 text-[#8B5CF6]'];
  return colors[index % colors.length];
};
// List of known subject tags
const subjectTags = ['Math', 'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Art', 'Music', 'Computer Science', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Hindi', 'Sanskrit', 'Urdu', 'Telugu', 'Tamil', 'Marathi'];
const PublicAssessmentCard = ({
  assessment,
  onPreview
}) => {
  const IconComponent = getIconForAssessment(assessment.title, assessment.tags || []);
  const iconColor = getIconColor(assessment.title, assessment.tags || []);
  const handleCardClick = () => {
    onPreview(assessment);
  };
  // Filter to get only the first subject tag
  const subjectTag = assessment.tags?.find(tag => subjectTags.some(subject => tag.toLowerCase().includes(subject.toLowerCase())));
  return <Tooltip.Provider delayDuration={200} data-id="element-1981">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.3
    }} whileHover={{
      y: -6
    }} onClick={handleCardClick} className="h-[240px] bg-surface-elevated border-2 border-custom hover:border-[#00B75F] rounded-2xl hover:shadow-custom-lg transition-all duration-300 relative flex flex-col overflow-hidden cursor-pointer group" data-id="element-1982">
        {/* Top Section - Icon and Title */}
        <div className="min-h-[140px] flex flex-col items-center justify-center px-4 pt-4 pb-3" data-id="element-1983">
          {/* Icon with subtle shadow and background */}
          <motion.div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300" style={{
          backgroundColor: `${iconColor}15`
        }} data-id="element-1984">
            <IconComponent size={32} style={{
            color: iconColor
          }} data-id="element-1985" />
          </motion.div>
          {/* Title - Now with more space */}
          <h3 className="font-bold text-text-primary text-center text-base leading-tight overflow-hidden text-ellipsis line-clamp-3 w-full" title={assessment.title} data-id="element-1986">
            {assessment.title}
          </h3>
        </div>

        {/* Bottom Section - Subject Tag, Creator and Stats */}
        <div className="h-[100px] border-t-2 border-custom px-4 py-3 bg-surface bg-opacity-50 flex flex-col gap-2" data-id="element-1987">
          {/* Subject Tag Row */}
          <div className="flex items-center justify-center" data-id="element-1988">
            {subjectTag ? <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-lg ${getTagColor(0)}`} data-id="element-1989">
                {subjectTag}
              </span> : <span className="text-xs text-text-tertiary italic" data-id="element-1990">
                No subject
              </span>}
          </div>

          {/* Creator and Stats Row */}
          <div className="flex items-center justify-between" data-id="element-1991">
            {/* Creator */}
            <div className="flex flex-col justify-center flex-1 min-w-0 pr-3" data-id="element-1992">
              <p className="text-xs text-text-tertiary mb-0.5" data-id="element-1993">Created by</p>
              <p className="font-semibold text-text-primary text-sm overflow-hidden text-ellipsis whitespace-nowrap" title={assessment.creator} data-id="element-1994">
                {assessment.creator}
              </p>
            </div>

            {/* Stats - Icon Only with Tooltips */}
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1995">
              {/* Questions */}
              <Tooltip.Root data-id="element-1996">
                <Tooltip.Trigger asChild data-id="element-1997">
                  <div className="flex flex-col items-center gap-1 cursor-help" data-id="element-1998">
                    <ListChecksIcon size={16} className="text-text-tertiary" data-id="element-1999" />
                    <span className="text-sm font-bold text-text-primary" data-id="element-2000">
                      {assessment.questions}
                    </span>
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Portal data-id="element-2001">
                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-2002">
                    Questions
                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2003" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              {/* Uses (Share + Duplicate count) */}
              <Tooltip.Root data-id="element-2004">
                <Tooltip.Trigger asChild data-id="element-2005">
                  <div className="flex flex-col items-center gap-1 cursor-help" data-id="element-2006">
                    <HeartIcon size={16} className="text-text-tertiary" data-id="element-2007" />
                    <span className="text-sm font-bold text-text-primary" data-id="element-2008">
                      {assessment.uses || assessment.attempts}
                    </span>
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Portal data-id="element-2009">
                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-2010">
                    Times shared or duplicated
                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2011" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </div>
          </div>
        </div>
      </motion.div>
    </Tooltip.Provider>;
};
export default PublicAssessmentCard;